import * as React from 'react';

type PressableCardKeyboardNavigationProps = {
    onFocusCapture: React.FocusEventHandler<HTMLElement>;
    onKeyDownCapture: React.KeyboardEventHandler<HTMLElement>;
};

const MANAGED_CARD_CONTROL_ATTRIBUTE = 'data-spine-card-keyboard-control';

const CARD_CONTROL_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    `[${MANAGED_CARD_CONTROL_ATTRIBUTE}]`,
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const TEXT_INPUT_SELECTOR = 'input, textarea, [contenteditable="true"]';

/**
 * Keyboard navigation for pressable cards with secondary focusable controls.
 */
export function usePressableCardKeyboardNavigation<T extends HTMLElement>(
    ref: React.RefObject<T | null>
): PressableCardKeyboardNavigationProps {
    const getControls = React.useCallback(() => {
        const root = ref.current;
        if (!root) {
            return [];
        }

        return Array.from(root.querySelectorAll<HTMLElement>(CARD_CONTROL_SELECTOR)).filter(
            (element) => {
                if (element === root || element.closest('[aria-hidden="true"]')) {
                    return false;
                }

                return (
                    !element.hasAttribute('disabled') &&
                    element.getAttribute('aria-disabled') !== 'true'
                );
            }
        );
    }, [ref]);

    const syncTabStops = React.useCallback(() => {
        for (const control of getControls()) {
            control.setAttribute(MANAGED_CARD_CONTROL_ATTRIBUTE, '');
            if (control.tabIndex !== -1) {
                control.tabIndex = -1;
            }
        }
    }, [getControls]);

    React.useEffect(() => {
        syncTabStops();
    }, [syncTabStops]);

    const onFocusCapture = React.useCallback(() => {
        syncTabStops();
    }, [syncTabStops]);

    const onKeyDownCapture = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.defaultPrevented) {
                return;
            }

            const target = event.target as HTMLElement;
            const root = ref.current;
            if (!root || !root.contains(target)) {
                return;
            }

            const controls = getControls();
            if (
                (event.key === 'Enter' || event.key === ' ') &&
                target !== root &&
                controls.some((control) => control.contains(target))
            ) {
                event.stopPropagation();
                return;
            }

            if (!['ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp'].includes(event.key)) {
                return;
            }

            if (target.closest(TEXT_INPUT_SELECTOR)) {
                return;
            }

            if (controls.length === 0) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const isBackward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
            const focusedControlIndex = controls.findIndex((control) => control.contains(target));

            if (target === root) {
                const control = isBackward ? controls.at(-1) : controls[0];
                control?.focus();
                return;
            }

            const nextIndex = focusedControlIndex + (isBackward ? -1 : 1);
            const nextControl = controls[nextIndex];
            if (nextControl) {
                nextControl.focus();
            } else {
                root.focus();
            }
        },
        [getControls, ref]
    );

    return { onFocusCapture, onKeyDownCapture };
}
